import { useDispatch } from 'react-redux'
import { useCallback, useState, useMemo } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { actions, CreateResumeWithLinkedInSteps } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useI18n } from 'builder/hooks/useI18n'
import { FetchStatuses } from 'builder/modules/constants'
import { LOCALIZATION_KEY, linkedInProfileRegex } from './constants'

export const useCreateResumeWithLinkedin = () => {
  const dispatch = useDispatch()
  const [linkedInFetchError, setLinkedInFetchError] = useState<string | null>(null)
  const [linkValidationStatus, setLinkValidationStatus] = useState<FetchStatuses>(
    FetchStatuses.notAsked,
  )

  const { i18n } = useI18n()
  const { resume, createResumeWithLinkedIn } = useTypedSelector(store => store.resumeEditor)

  const {
    linkedInProfileURL,
    fetchLinkedInProfileStatus,
    currentStep,
    linkedInProfileData,
    errorState,
    updateResumeWithLinkedInStatus,
  } = createResumeWithLinkedIn

  const handleUseAnotherLink = () => {
    dispatch(
      actions.updateCreateResumeWithLinkedInState({
        currentStep: CreateResumeWithLinkedInSteps.linkedInURL,
        linkedInProfileURL: '',
        updateResumeWithLinkedInStatus: FetchStatuses.notAsked,
        linkedInProfileData: null,
      }),
    )
    trackInternalEvent('click_use_another_link', {
      label: 'profile_screen_li_prefill',
    })
  }

  const modifiedURL = useMemo(() => {
    let updatedUrl = linkedInProfileURL.toLowerCase()
    // If protocol was not provided, add it.
    updatedUrl =
      updatedUrl.startsWith('https') || updatedUrl.startsWith('http')
        ? updatedUrl
        : `https://${updatedUrl}`
    // The regular expression doesn't match the URL if it doesn't start with www.
    // So, we need to add www. to the URL if it doesn't have it.
    updatedUrl = updatedUrl.replace(/:\/\/(?!www\.)/, '://www.')
    return updatedUrl
  }, [linkedInProfileURL])

  const handleGenerateResumeWithLinkedIn = useCallback(() => {
    if (!modifiedURL || !resume?.id) return
    dispatch(
      actions.updateResumeWithLinkedInProfile({
        linkedInProfileURL: modifiedURL,
        resumeId: resume?.id,
      }),
    )
    trackInternalEvent('click_yes', {
      label: 'profile_screen_li_prefill',
    })
  }, [modifiedURL, resume?.id, dispatch])

  const handleFetchLinkedInProfile = useCallback(() => {
    if (linkedInFetchError) {
      return
    }
    if (linkedInProfileURL === '') {
      setLinkedInFetchError(i18n.t(`${LOCALIZATION_KEY}.prefill_modal.input_error_empty`))
    } else {
      dispatch(actions.fetchCreateResumeWithLinkedInProfile({ linkedInProfileURL: modifiedURL }))
    }
    trackInternalEvent('click_continue', {
      label: 'linkedin_prefill_screen_li_prefill',
    })
  }, [dispatch, i18n, linkedInFetchError, linkedInProfileURL, modifiedURL])

  const handleBack = useCallback(() => {
    dispatch(
      actions.updateCreateResumeWithLinkedInState({
        errorState: false,
        fetchLinkedInProfileStatus: FetchStatuses.notAsked,
      }),
    )
  }, [dispatch])

  const handleReturnStart = useCallback(() => {
    dispatch(actions.resetCreateResumeWithLinkedInFlow())
    dispatch(
      actions.updateSimpleField({
        name: 'showPrefillResumeModal',
        value: true,
        debounce: false,
      }),
    )
    trackInternalEvent('click_return', {
      label: 'linkedin_profile_error_screen_li_prefill',
    })
  }, [dispatch])

  const handlePrefillBack = useCallback(() => {
    dispatch(actions.resetCreateResumeWithLinkedInFlow())
    dispatch(
      actions.updateSimpleField({
        name: 'showPrefillResumeModal',
        value: true,
        debounce: false,
      }),
    )
  }, [dispatch])

  const handleTryAgain = useCallback(() => {
    trackInternalEvent('click_try_again', {
      label: 'linkedin_profile_error_screen_li_prefill',
    })
    if (
      currentStep === CreateResumeWithLinkedInSteps.linkedInURL ||
      currentStep === CreateResumeWithLinkedInSteps.linkedInProfile
    ) {
      dispatch(
        actions.updateCreateResumeWithLinkedInState({
          currentStep: CreateResumeWithLinkedInSteps.linkedInURL,
          linkedInProfileURL: '',
          updateResumeWithLinkedInStatus: FetchStatuses.notAsked,
          linkedInProfileData: null,
          errorState: false,
        }),
      )
    } else {
      handleReturnStart()
    }
  }, [currentStep, dispatch, handleReturnStart])

  const isLinkValid = linkedInProfileRegex.test(linkedInProfileURL.toLowerCase())

  const handleChangeURL = useCallback(
    (event: { target: { value: string } }) => {
      const { value } = event.target
      dispatch(
        actions.updateCreateResumeWithLinkedInState({
          linkedInProfileURL: value,
        }),
      )
      if (!value) {
        setLinkValidationStatus(FetchStatuses.notAsked)
        return
      }

      if (linkValidationStatus !== FetchStatuses.loading) {
        setLinkValidationStatus(FetchStatuses.loading)
      }
    },
    [dispatch, linkValidationStatus],
  )

  const handleClose = useCallback(() => {
    dispatch(actions.resetCreateResumeWithLinkedInFlow())
  }, [dispatch])

  return {
    linkedInProfileURL,
    fetchLinkedInProfileStatus,
    currentStep,
    handleUseAnotherLink,
    handleGenerateResumeWithLinkedIn,
    handleTryAgain,
    handleFetchLinkedInProfile,
    setLinkedInFetchError,
    linkedInFetchError,
    handleBack,
    isLinkValid,
    handleChangeURL,
    linkValidationStatus,
    setLinkValidationStatus,
    linkedInProfileData,
    handleReturnStart,
    errorState,
    updateResumeWithLinkedInStatus,
    handlePrefillBack,
    handleClose,
  }
}
